import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Download, Printer, Loader2, FileText, X, Sparkles, ZoomIn, ZoomOut } from "lucide-react";
import { DEFAULT_TERMS } from "@/components/QuoteTemplate";
import PdfPagesView from "@/components/PdfPagesView";
import { buildQuotePdf, printPdf, quoteFileName } from "@/lib/quotePdf";
import api from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const LS_KEY = "ge_quote_view_opts";
const DEF = { detailed: true, showCodes: false, showBrand: true, showBrands: true, singlePage: false };
const ZOOMS = [0.75, 1, 1.25, 1.5, 2];
const BASE_W = 794;
const loadOpts = () => { try { return { ...DEF, ...JSON.parse(localStorage.getItem(LS_KEY) || "{}") }; } catch { return { ...DEF }; } };

/** Teklif önizleme — ekranda gösterilen, indirilen ve yazdırılan belge AYNI PDF'tir (vektörel, birebir). */
export default function QuotePreviewDialog({ quote, onClose, terms, children }) {
  const [opts, setOpts] = useState(loadOpts);
  const [busy, setBusy] = useState(false);
  const [tms, setTms] = useState(terms || null);
  const [pdfData, setPdfData] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [zoom, setZoom] = useState(1);
  const pdfRef = useRef(null);
  const zi = ZOOMS.indexOf(zoom);
  const zoomBy = (d) => setZoom(ZOOMS[Math.min(ZOOMS.length - 1, Math.max(0, zi + d))]);
  useEffect(() => { localStorage.setItem(LS_KEY, JSON.stringify(opts)); }, [opts]);
  useEffect(() => {
    if (terms) { setTms(terms); return; }
    api.get("/settings/quote-terms").then((r) => setTms(r.data.terms || DEFAULT_TERMS)).catch(() => setTms(DEFAULT_TERMS));
  }, [terms]);
  const set = (k) => (v) => setOpts((o) => ({ ...o, [k]: v }));

  // Teklif / seçenekler değişince PDF yeniden üretilir (önizleme = indirilecek belge)
  useEffect(() => {
    if (!quote || tms === null) { setPdfData(null); pdfRef.current = null; return; }
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const pdf = await buildQuotePdf(quote, { ...opts, terms: tms || [] });
        if (cancelled) return;
        pdfRef.current = pdf;
        setPageCount(pdf.getNumberOfPages());
        setPdfData(pdf.output("arraybuffer"));
      } catch (e) { console.error(e); if (!cancelled) toast.error("Önizleme oluşturulamadı"); }
      finally { if (!cancelled) setBusy(false); }
    }, 120);
    return () => { cancelled = true; clearTimeout(t); };
  }, [quote, opts, tms]);

  const ready = () => { if (!pdfRef.current) { toast.error("PDF henüz hazır değil"); return null; } return pdfRef.current; };
  const download = () => { const pdf = ready(); if (!pdf) return; const name = quoteFileName(quote); try { pdf.setProperties({ title: name }); } catch { /* noop */ } pdf.save(`${name}.pdf`); toast.success(`${name}.pdf indirildi`); };
  const print = () => { const pdf = ready(); if (!pdf) return; printPdf(pdf, quoteFileName(quote), { asFileName: true }); };

  const Toggle = ({ k, label, testid }) => (
    <label className={cn("inline-flex items-center gap-1.5 text-xs font-semibold cursor-pointer select-none rounded-full border px-2.5 py-1 transition-colors", opts[k] ? "bg-primary/10 border-primary/30 text-primary" : "bg-card text-muted-foreground hover:text-foreground")}>
      <Switch checked={!!opts[k]} onCheckedChange={set(k)} data-testid={testid} className="scale-75 -ml-1" />{label}
    </label>
  );

  return (
    <Dialog open={!!quote} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-[96vw] sm:max-w-5xl max-h-[95vh] p-0 gap-0 overflow-hidden flex flex-col" data-testid="quote-preview">
        <div className="shrink-0 relative bg-[#0B192C] text-white px-4 sm:px-5 py-3 overflow-hidden no-print">
          <span className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full bg-primary/30 blur-3xl" />
          <span className="pointer-events-none absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-primary via-orange-300 to-primary" />
          <div className="relative flex flex-wrap items-center gap-2">
            <span className="h-9 w-9 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center"><FileText className="h-4 w-4 text-[#ffb27a]" /></span>
            <div className="mr-auto min-w-0">
              <DialogTitle className="font-head text-lg font-black tracking-tight leading-tight flex items-center gap-2">
                Teklif Önizleme
                <span className="font-mono text-sm font-bold text-[#ffb27a] bg-white/10 rounded px-1.5 py-0.5">{quote?.quote_no}</span>
                {pageCount > 0 && <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300 bg-white/10 rounded-full px-2 py-0.5" data-testid="quote-preview-pages">{pageCount} sayfa</span>}
              </DialogTitle>
              <p className="text-[11px] text-slate-300 truncate hidden sm:block">{quote?.customer_title} · Gördüğünüz belge, indirilen / yazdırılan PDF ile birebir aynıdır.</p>
            </div>
            <Button size="sm" onClick={download} disabled={busy || !pdfData} data-testid="pdf-btn" className="shadow-md">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}PDF İndir</Button>
            <Button size="sm" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white" onClick={print} disabled={busy || !pdfData} data-testid="print-btn"><Printer className="h-4 w-4 mr-1.5" />Yazdır</Button>
            <button type="button" onClick={() => onClose && onClose()} className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="quote-preview-close" title="Kapat"><X className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="shrink-0 border-b bg-card px-4 sm:px-5 py-2 flex flex-wrap items-center gap-2 no-print">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground mr-1 font-head"><Sparkles className="h-3 w-3 text-primary" />Görünüm</span>
          <Toggle k="detailed" label="İskonto sütunları" testid="quote-detailed-toggle" />
          <Toggle k="showBrand" label="Marka Adı" testid="quote-brand-toggle" />
          <Toggle k="showCodes" label="Ürün kodu sütunu" testid="quote-codes-toggle" />
          <Toggle k="showBrands" label="Marka logoları" testid="quote-logos-toggle" />
          <Toggle k="singlePage" label="Tek sayfaya sığdır" testid="pdf-single-page-toggle" />
          <div className="inline-flex items-center rounded-full border bg-card overflow-hidden" data-testid="quote-zoom">
            <button type="button" onClick={() => zoomBy(-1)} disabled={zi <= 0} className="h-7 w-7 inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40" title="Küçült" data-testid="quote-zoom-out"><ZoomOut className="h-3.5 w-3.5" /></button>
            <span className="text-[11px] font-bold tabular-nums w-11 text-center font-mono" data-testid="quote-zoom-value">{Math.round(zoom * 100)}%</span>
            <button type="button" onClick={() => zoomBy(1)} disabled={zi >= ZOOMS.length - 1} className="h-7 w-7 inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40" title="Büyüt" data-testid="quote-zoom-in"><ZoomIn className="h-3.5 w-3.5" /></button>
          </div>
          {children && <div className="ml-auto flex flex-wrap items-center gap-1.5" data-testid="quote-preview-actions">{children}</div>}
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-4 sm:p-6 bg-[radial-gradient(circle_at_top,_#e2e8f0_0%,_#cbd5e1_100%)]">
          {pdfData ? <PdfPagesView data={pdfData} width={Math.round(BASE_W * zoom)} testid="quote-pdf" />
            : <div className="flex items-center justify-center py-32 text-sm text-muted-foreground" data-testid="quote-preview-loading"><Loader2 className="h-5 w-5 animate-spin mr-2" />Teklif belgesi hazırlanıyor…</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
